/**
 * Patient → Clinic Consultation → Search (subscribed patients only).
 *
 * PIN + Specialty filter that hits /api/patient/clinic/search. Results are
 * rendered as a list and (when Leaflet is loaded) on a map underneath. We use
 * the global window.L (Leaflet UMD bundled in index.html) so this file has zero
 * extra dependencies — when we swap to Google Maps later only the map block
 * changes.
 */
import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Phone, Clock, BadgeCheck, IndianRupee, X, Calendar as CalendarIcon, CheckCircle2, Building2, Hospital, BellRing } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "@/lib/toast";
import { pay } from "@/lib/payment";
import { useAuth } from "@/context/AuthContext";

const SPECIALTIES = [
  "Oncology", "Cardiology", "Neurology", "Orthopaedics", "Nephrology",
  "Gastroenterology", "Endocrinology", "Pulmonology", "OBGYN", "Dermatology", "Ophthalmology",
];

const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtDate(d) {
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

/* Booking modal: pick an in-person slot at an establishment, pay, confirm. */
function ClinicBookingModal({ result, discountPercent, onClose, onBooked }) {
  const { user } = useAuth();
  const clinic = result.clinic;
  const slotDays = Object.keys(clinic.slots || {}).filter((d) => (clinic.slots[d] || []).length);
  // Next 21 days that fall on a weekday the establishment offers.
  const dates = [];
  for (let i = 0; i < 21 && dates.length < 12; i++) {
    const dt = new Date(); dt.setDate(dt.getDate() + i);
    if (slotDays.includes(WEEKDAY_ABBR[dt.getDay()])) dates.push(dt);
  }
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [wlJoined, setWlJoined] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  const iso = (d) => d.toISOString().slice(0, 10);

  useEffect(() => {
    if (!date) return;
    setTime(""); setSlots([]); setWlJoined(false);
    api.get(`/patient/clinic/${result.id}/${clinic.id}/slots`, { params: { date: iso(date) } })
      .then((r) => setSlots(r.data.slots || []))
      .catch((e) => toast(formatApiError(e.response?.data?.detail)));
  }, [date]);

  const joinWaitlist = async () => {
    setWlBusy(true);
    try {
      await api.post("/waitlist", { doctor_id: result.id, date: iso(date), clinic_id: clinic.id });
      setWlJoined(true);
      toast("You're on the waitlist — we'll notify you if a slot opens ✓");
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "Couldn't join the waitlist.");
    } finally { setWlBusy(false); }
  };

  const confirm = async () => {
    if (!date || !time) { toast("Please pick a date and time"); return; }
    setBusy(true);
    try {
      const proof = await pay(api, {
        description: `Clinic visit · ${clinic.name}`,
        name: user?.name, email: user?.email, consult_type: "Clinic visit",
        doctor_id: result.id, clinic_id: clinic.id,
      });
      const { data } = await api.post("/patient/clinic/book", {
        doctor_id: result.id, clinic_id: clinic.id, date: iso(date), time,
        symptom: reason, order_id: proof.order_id,
        payment_id: proof.payment_id, signature: proof.signature,
      });
      setDone(data.appointment);
      onBooked?.();
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || e.message || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose} data-testid="clinic-book-modal">
      <div className="modal clinic-book-card" onClick={(e) => e.stopPropagation()}>
        <button className="m-close" onClick={onClose} data-testid="clinic-book-close">✕</button>
        {done ? (
          <div style={{ textAlign: "center", padding: "10px 6px" }} data-testid="clinic-book-success">
            <CheckCircle2 size={44} color="#1D9E75" style={{ margin: "0 auto 10px" }} />
            <h3 className="h" style={{ fontSize: 21, margin: "0 0 6px" }}>Visit confirmed</h3>
            <p className="lead">Your in-person consultation with <b>{result.name}</b> at <b>{clinic.name}</b> is booked for <b>{done.date} · {done.time}</b>. You'll find it under My appointments.</p>
            <button className="btn btn-green full" style={{ marginTop: 14 }} onClick={onClose} data-testid="clinic-book-done">Done</button>
          </div>
        ) : (
          <>
            <div className="cb-head">
              <span className="estab-icon">{clinic.establishment_type === "Hospital" ? <Hospital size={18} /> : <Building2 size={18} />}</span>
              <div>
                <h3 className="h" style={{ fontSize: 19, margin: 0 }}>Book a clinic visit</h3>
                <p className="lead" style={{ margin: "2px 0 0", fontSize: 13 }}>{result.name} · {clinic.name}</p>
              </div>
            </div>
            <div className="cb-addr"><MapPin size={13} /> {clinic.address}{clinic.pincode ? ` · ${clinic.pincode}` : ""}</div>

            <label className="cb-label"><CalendarIcon size={14} /> Choose a date</label>
            <div className="cb-dates" data-testid="clinic-book-dates">
              {dates.length === 0 && <p className="lead" style={{ fontSize: 13 }}>This establishment hasn't published any slots yet.</p>}
              {dates.map((d) => (
                <button key={iso(d)} className={`cb-date ${date && iso(date) === iso(d) ? "on" : ""}`}
                  onClick={() => setDate(d)} data-testid={`clinic-book-date-${iso(d)}`}>{fmtDate(d)}</button>
              ))}
            </div>

            {date && (
              <>
                <label className="cb-label"><Clock size={14} /> Choose a time</label>
                <div className="cb-times" data-testid="clinic-book-times">
                  {slots.length === 0 && <p className="lead" style={{ fontSize: 13 }}>No slots available on this day.</p>}
                  {slots.map((s) => (
                    <button key={s.time} disabled={!s.available}
                      className={`cb-time ${time === s.time ? "on" : ""} ${!s.available ? "off" : ""}`}
                      onClick={() => setTime(s.time)} data-testid={`clinic-book-time-${s.time}`}>{s.time}</button>
                  ))}
                </div>
                {slots.length > 0 && slots.every((s) => !s.available) && (
                  wlJoined ? (
                    <p className="lead" style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600, marginTop: 6 }} data-testid="clinic-waitlist-joined">
                      <BellRing size={13} /> You're on the waitlist — we'll notify you when a slot opens.
                    </p>
                  ) : (
                    <button className="btn btn-ghost full" style={{ marginTop: 8 }} onClick={joinWaitlist} disabled={wlBusy} data-testid="clinic-waitlist-btn">
                      <BellRing size={14} /> &nbsp;{wlBusy ? "Adding you…" : "Notify me when a slot opens"}
                    </button>
                  )
                )}
              </>
            )}

            <label className="cb-label">Reason for visit (optional)</label>
            <textarea className="in" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly describe your concern" data-testid="clinic-book-reason" />

            <div className="cb-fee">
              <span>Consultation fee</span>
              {(() => {
                const base = parseInt(String(clinic.fee || result.consult_fee || "999").replace(/\D/g, ""), 10) || 999;
                return discountPercent > 0
                  ? <span><s style={{ color: "#9fb3c8" }}>₹{base}</s> <b style={{ color: "#0F6E56" }}>₹{Math.round(base * (1 - discountPercent / 100))}</b> <small>({discountPercent}% off)</small></span>
                  : <b>₹{base}</b>;
              })()}
            </div>
            <button className="btn btn-green full" onClick={confirm} disabled={busy || !time} data-testid="clinic-book-confirm">
              {busy ? "Processing…" : "Pay & confirm visit"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ClinicSearch({ discountPercent = 0 }) {
  const [pincode, setPincode] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // null until first search
  const [center, setCenter] = useState(null);
  const [booking, setBooking] = useState(null);
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markersRef = useRef([]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!pincode.trim()) {
      toast("Please enter a PIN code or area name");
      return;
    }
    setBusy(true);
    try {
      const params = { pincode: pincode.trim(), radius_km: 15 };
      if (specialty) params.specialty = specialty;
      const { data } = await api.get("/patient/clinic/search", { params });
      setResults(data.results || []);
      setCenter(data.center || null);
    } catch (err) {
      toast(formatApiError(err.response?.data?.detail) || "Search failed");
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  // Render / update Leaflet map whenever search results change.
  useEffect(() => {
    if (!center || !window.L || !mapRef.current) return;
    const L = window.L;
    if (!mapInst.current) {
      mapInst.current = L.map(mapRef.current, { scrollWheelZoom: false })
        .setView([center.lat, center.lng], 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(mapInst.current);
    } else {
      mapInst.current.setView([center.lat, center.lng], 12);
    }
    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    // Centre pin (patient location)
    const centerIcon = L.divIcon({
      className: "clinic-pin-center",
      html: '<div style="background:#185FA5;color:#fff;border-radius:50%;width:22px;height:22px;display:grid;place-items:center;font-size:11px;font-weight:700;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">Me</div>',
      iconSize: [22, 22], iconAnchor: [11, 11],
    });
    markersRef.current.push(L.marker([center.lat, center.lng], { icon: centerIcon }).addTo(mapInst.current));
    // Clinic pins
    (results || []).forEach((r) => {
      const ic = L.divIcon({
        className: "clinic-pin",
        html: '<div style="background:#1D9E75;color:#fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);width:26px;height:26px;display:grid;place-items:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"><div style="transform:rotate(45deg);font-size:12px;font-weight:700">+</div></div>',
        iconSize: [26, 26], iconAnchor: [13, 24],
      });
      const m = L.marker([r.clinic.lat, r.clinic.lng], { icon: ic }).addTo(mapInst.current);
      m.bindPopup(
        `<b>${r.name}</b><br/>${r.specialty}<br/>${r.clinic.name}<br/>` +
        `<small>${r.clinic.address || ""}</small><br/>` +
        `<small><b>${r.distance_km} km</b> away</small>`,
      );
      markersRef.current.push(m);
    });
  }, [results, center]);

  // Clean up Leaflet map on unmount.
  useEffect(() => () => {
    if (mapInst.current) { mapInst.current.remove(); mapInst.current = null; }
  }, []);

  return (
    <div className="clinic-search-wrap" data-testid="clinic-search">
      {discountPercent > 0 && (
        <div className="sub-discount-banner" data-testid="clinic-discount-banner">
          <BadgeCheck size={16} /> Premium subscriber benefit · <b>{discountPercent}% off</b> every booking, automatically applied.
        </div>
      )}

      <div className="clinic-search-card">
        <h3 style={{ marginTop: 0, fontFamily: "'Fraunces',serif", fontWeight: 540 }}>
          Find a clinic near you
        </h3>
        <p className="sub" style={{ marginTop: 0, marginBottom: 18 }}>
          Premium-subscriber doctors with verified clinics within 15 km of your PIN code.
        </p>
        <form className="clinic-search-form" onSubmit={runSearch}>
          <div className="field">
            <label>PIN code or area</label>
            <input
              className="in"
              placeholder="e.g. 560034 or Koramangala"
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              data-testid="clinic-pin-input"
            />
          </div>
          <div className="field">
            <label>Specialty (optional)</label>
            <select
              className="in"
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              data-testid="clinic-specialty-select"
            >
              <option value="">All specialties</option>
              {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <button
            type="submit"
            className="btn-search"
            disabled={busy}
            data-testid="clinic-search-submit"
          >
            <Search size={16} /> {busy ? "Searching…" : "Search"}
          </button>
        </form>
      </div>

      {results !== null && (
        <>
          {center && (
            <div ref={mapRef} className="clinic-map" data-testid="clinic-map" />
          )}
          {results.length === 0 ? (
            <div className="clinic-empty" data-testid="clinic-empty">
              No subscribed clinics found within 15 km of {center?.display_name || pincode}.
              Try a nearby PIN code or remove the specialty filter.
            </div>
          ) : (
            <div className="clinic-results" data-testid="clinic-results">
              {results.map((r) => (
                <div className="clinic-card" key={`${r.id}-${r.clinic_id}`} data-testid={`clinic-card-${r.id}`}>
                  <div className="cc-photo">
                    {r.photo_id
                      ? <img src={`${API}/public/image/${r.photo_id}`} alt="" />
                      : <span style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 600 }}>{r.name?.replace("Dr. ", "").charAt(0) || "?"}</span>}
                  </div>
                  <div className="cc-head">
                    <h4 className="cc-name">{r.name}</h4>
                    <p className="cc-spec">{r.specialty}{r.reg_no ? ` · ${r.reg_no}` : ""}</p>
                    <div className="cc-clinic">{r.clinic.name}</div>
                    <div className="cc-addr">
                      <MapPin size={12} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />
                      {r.clinic.address}{r.clinic.pincode ? ` · ${r.clinic.pincode}` : ""}
                    </div>
                    <div className="cc-meta">
                      {r.clinic.phone && <span><Phone size={12} /> {r.clinic.phone}</span>}
                      {r.clinic.hours && <span><Clock size={12} /> {r.clinic.hours}</span>}
                      {r.consult_fee && <span><IndianRupee size={12} /> {r.consult_fee}</span>}
                    </div>
                  </div>
                  <div className="cc-right">
                    <span className="cc-dist" data-testid={`clinic-card-dist-${r.id}`}>
                      {r.distance_km} km away
                    </span>
                    <button
                      className="cc-book"
                      onClick={() => setBooking(r)}
                      data-testid={`clinic-book-${r.id}`}
                    >
                      Book visit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {booking && (
        <ClinicBookingModal
          result={booking}
          discountPercent={discountPercent}
          onClose={() => setBooking(null)}
          onBooked={() => {}}
        />
      )}
    </div>
  );
}
